import type { ReactNode } from "react";
import { AsyncButton } from "../ui/async-button";
import { LlmModelSelect } from "./LlmModelSelect";

type ModelOption = { value: string; label: string };

/**
 * The extract/analyze action cluster shared by the 4 Chapter panels and the 4
 * Guide panels: a model picker + a primary "Extract" AsyncButton, plus optional
 * "Analyze book" and "Add" actions. Designed to slot into `PanelHeader`'s
 * `actions` prop so headers stay consistent across panels.
 */
export function ExtractToolbar({
  model,
  onModelChange,
  openAiModels,
  anthropicModels,
  onExtract,
  extractBusy = false,
  extractLabel = "Extract",
  extractBusyLabel = "Extracting…",
  onAnalyzeBook,
  analyzeBusy = false,
  analyzeLabel = "Analyze book",
  analyzeBusyLabel = "Analyzing book…",
  onAdd,
  addLabel = "Add",
  addLeading,
  disabled = false,
  modelClassName = "w-44",
}: {
  model: string;
  onModelChange: (model: string, provider: "openai" | "anthropic") => void;
  openAiModels: readonly ModelOption[];
  anthropicModels: readonly ModelOption[];
  onExtract: () => void;
  extractBusy?: boolean;
  extractLabel?: ReactNode;
  extractBusyLabel?: ReactNode;
  onAnalyzeBook?: () => void;
  analyzeBusy?: boolean;
  analyzeLabel?: ReactNode;
  analyzeBusyLabel?: ReactNode;
  onAdd?: () => void;
  addLabel?: ReactNode;
  addLeading?: ReactNode;
  disabled?: boolean;
  modelClassName?: string;
}) {
  const anyBusy = disabled || extractBusy || analyzeBusy;
  return (
    <>
      <LlmModelSelect
        value={model}
        onChange={onModelChange}
        openAiModels={openAiModels}
        anthropicModels={anthropicModels}
        className={modelClassName}
        title="Analysis model"
        aria-label="Analysis model"
        disabled={anyBusy}
      />
      <AsyncButton
        size="sm"
        type="button"
        onClick={onExtract}
        busy={extractBusy}
        busyLabel={extractBusyLabel}
        disabled={anyBusy}
      >
        {extractLabel}
      </AsyncButton>
      {onAnalyzeBook ? (
        <AsyncButton
          size="sm"
          variant="secondary"
          type="button"
          onClick={onAnalyzeBook}
          busy={analyzeBusy}
          busyLabel={analyzeBusyLabel}
          disabled={anyBusy}
        >
          {analyzeLabel}
        </AsyncButton>
      ) : null}
      {onAdd ? (
        <AsyncButton
          size="sm"
          variant="secondary"
          type="button"
          onClick={onAdd}
          disabled={anyBusy}
        >
          {addLeading}
          {addLabel}
        </AsyncButton>
      ) : null}
    </>
  );
}
